import { generateRandomNumericId } from '../../../shared/helpers/generate-random-numeric-id/generate-random-numeric-id.helper';

export type DocumentType = RegularDocumentType | ComplianceDocumentType;

export enum ComplianceDocumentTypeAttributeType {
  ComplianceFile = 'ComplianceFile',
  ComplianceFileExpiry = 'ComplianceFileExpiry',
}

export interface ComplianceDocumentType {
  id: number;
  attributeType: ComplianceDocumentTypeAttributeType;
  category: Category.Compliance;
  attributeName: string;
  name: string;
  isVisible: boolean;
}
export interface RegularDocumentType {
  id: number;
  category: Category.ProductAndService | Category.Commercial;
  used: boolean;
  name: string;
  isVisible: boolean;
}

export enum Category {
  Compliance = 'Compliance',
  ProductAndService = 'ProductAndService',
  Commercial = 'Commercial',
}

export type CurrentCategory =
  | Category.Compliance
  | Category.ProductAndService
  | Category.Commercial;

export function isCompliance(
  documentType: DocumentType,
): documentType is ComplianceDocumentType {
  return documentType.category === Category.Compliance;
}

export function isRegular(
  documentType: DocumentType,
): documentType is RegularDocumentType {
  return isProductAndService(documentType) || isCommercial(documentType);
}

export function isProductAndService(
  documentType: DocumentType,
): documentType is RegularDocumentType {
  return documentType.category === Category.ProductAndService;
}

export function isCommercial(
  documentType: DocumentType,
): documentType is RegularDocumentType {
  return documentType.category === Category.Commercial;
}

const generateEmptyRegularDocumentType = (
  category: RegularDocumentType['category'],
): RegularDocumentType => ({
  id: generateRandomNumericId(),
  name: '',
  category,
  used: false,
  isVisible: true,
});

export const generateEmptyProductAndServiceDocumentType =
  (): RegularDocumentType =>
    generateEmptyRegularDocumentType(Category.ProductAndService);

export const generateEmptyCommercialDocumentType = (): RegularDocumentType =>
  generateEmptyRegularDocumentType(Category.Commercial);

export const createDocumentTypeOtherNamesSet = (
  idToFilterOut: number | null,
  complianceDocumentTypes: ComplianceDocumentType[],
  productAndServiceDocumentTypes: RegularDocumentType[],
  commercialDocumentTypes: RegularDocumentType[],
): Set<string> => {
  return new Set<string>(
    [
      ...complianceDocumentTypes
        .filter(({ id: idToCheck }) => idToFilterOut !== idToCheck)
        .map(({ name }) => name ?? ''),
      ...productAndServiceDocumentTypes
        .filter(({ id: idToCheck }) => idToFilterOut !== idToCheck)
        .map(({ name }) => name ?? ''),
      ...commercialDocumentTypes
        .filter(({ id: idToCheck }) => idToFilterOut !== idToCheck)
        .map(({ name }) => name ?? ''),
    ].filter((name) => name !== ''),
  );
};

export const isComplianceDocumentTypeValid = (
  complianceDocumentType: ComplianceDocumentType,
  documentTypeOtherNamesSet: Set<string>,
): boolean => !documentTypeOtherNamesSet.has(complianceDocumentType.name ?? '');

export const isRegularDocumentTypeValid = (
  documentType: RegularDocumentType,
  documentTypeOtherNamesSet: Set<string>,
): boolean =>
  (documentType.name ?? '') !== '' &&
  !documentTypeOtherNamesSet.has(documentType.name ?? '');

export const getDocumentTypeDisplayName = (
  documentType: DocumentType,
): string =>
  documentType.name?.trim() ||
  (isCompliance(documentType) ? documentType.attributeName.trim() : '');
